import {Feature} from '@/services/OverlaysService';
import {getStore} from '@/services/store';
import {getCollection} from '../../filters/filters';
import {deleteSubcollectionDocument} from '@/services/FirestoreService';
import {getFirestoreUpdateFromFeature} from '@/lib/feature-lib';
import {linkedLabelsOf} from '@/components/FeatureMenuItems/Folders/linkedLabelNesting';

// Types
import {FeatureFolderItem, FeatureManagerOptions, FeatureManagerUpdateData, Folder, FolderItem, ParentData} from './types';
import {Nullable} from '@/types/commonTypes';

export const isFolder = (folderItem: FolderItem): folderItem is Folder => {
  return (
    typeof folderItem === 'object' &&
    folderItem !== null &&
    'children' in folderItem
  );
};

/**
 * Flattens out a folder array to return a one dimensional array of
 * all features contained within the folder structure
 * @param arr - The array of nested folders to flatten
 * @return A map whose keys are feature IDs and values are the
 * path to that feature within the folders structure
 */
function _flattenFolderFeatures(arr: FolderItem[]): Map<string, number[]> {
  const output = new Map();
  function flatten(
    arr: FolderItem[],
    path: number[] = [],
  ): Array<{id: string; path: number[]}> {
    return arr.reduce<Array<{id: string; path: number[]}>>((acc, curr, index) => {
      if (curr.id) {
        acc.push({id: curr.id, path: path.concat(index)});
      }
      if (isFolder(curr)) {
        acc = acc.concat(flatten(curr.children, path.concat(index)));
      }
      return acc;
    }, []);
  }
  flatten(arr).map((i) => output.set(i.id, i.path));

  return output;
}

export class FeatureManager {
  id: string;
  private: boolean;
  visible: boolean;
  defaultVisibility: boolean;
  render: boolean;
  name: string;
  parent: Nullable<ParentData> = null;
  folders: FolderItem[];
  features: Map<string, Feature>;
  editMode: boolean;
  collectionName: string;
  featureCollectionName: string;
  constructor(options: FeatureManagerOptions) {
    this.id = options.id;
    /** Indicates whether the feature manager is private only to the user who created it */
    this.private = false;
    /** Indicates whether or not this feature manager should ever be seen */
    this.visible = true;
    /** Default value indicating whether or not this feature manager should be seen */
    this.defaultVisibility = options.defaultVisibility || false;
    /**
     * Indicates whether or not this feature manager should be rendered when in the map viewport,
     * Defaults to false until the map decides to render
     */
    this.render = false;
    /** The human-readable name */
    this.name = options.name || '';
    /** The folders of the feature manager. */
    this.folders = options.folders ?? [];
    /** The features of the feature manager. */
    this.features = options.features ?? new Map();
    /** Indicates whether or not this feature manager is feature manager is being edited */
    this.editMode = options.editMode ?? false;
    /** The Firestore collection name for the feature manager */
    this.collectionName = options.collectionName || 'beyondMaps-overlay';
    /** The local filter collection name (e.g. 'features', 'taxFeatures) */
    this.featureCollectionName = options.featureCollectionName || 'features';
  }

  async update(data: FeatureManagerUpdateData, updateEditedBy = true) {
    console.warn('FeatureManager.update not implemented.');
  }

  setRender(render: boolean) {
    console.warn('FeatureManager.setRender not implemented.');
  }

  /**
   * setDefaultVisibility sets the default visibility of this feature manager. This will persist the
   * visibility setting to the database and set the visibility of this feature manager for the current browser session.
   * @param visible
   * @return A promise that resolves when the visibility has been set.
   */
  async setDefaultVisibility(visible: boolean) {
    this.defaultVisibility = visible;
    return await this.update({defaultVisibility: visible});
  }

  /**
   * Turns edit mode on or off for this feature manager.
   * @param editMode true to turn edit mode on, false to turn it off
   */
  setEditMode(editMode: boolean) {
    this.editMode = editMode;
  }

  /**
   * newFeature creates a new feature for this feature manager and saves it to the database.
   * @param data The feature to add to this feature manager.
   * @param featureId - An optional document ID that a feature should use
   * @param folderPath - Whether or not to restore the feature to its folder
   * @param fromRestoredFolder - Special flag if being called from the folder restore function.
   * This is used to prevent pushing a "new feature" from the restore function to the root of folders unnecessarily.
   * @return A promise that resolves to the newly created feature.
   */
  async newFeature(
    data: object,
    featureId: string,
    folderPath: Nullable<number[]>,
    fromRestoredFolder: boolean,
  ): Promise<Nullable<Feature>> {
    console.warn('FeatureManager.newFeature not implemented.');
    return null; // Placeholder return, should be replaced with actual feature creation logic
  }

  /**
   * addFeature adds an existing feature to this feature manager object for tracking and control.
   * This does NOT create a new feature or modify this feature manager.
   * @param data The feature data to add to this feature manager.
   * @return A promise that resolves to the newly created feature.
   */
  addFeature(data: object, skipRenderOrder: boolean) : Promise<Feature | null> {
    console.warn('FeatureManager.addFeature not implemented.');
    return Promise.resolve(null); // Placeholder return, should be replaced with actual feature addition logic
  }


  /**
   * deletes the feature with the given ID from this feature manager. Returns a promise if the feature is being
   * deleted and false if the feature doesn't exist.
   * @param id The ID of the feature to delete.
   * @return A promise that resolves when the feature has been deleted or false
   * if the feature did not exist. If the feature was deleted, a function to restore it will be returned if possible.
   * The feature's linked labels are deleted with it, and restored with it.
   */
  async deleteFeature(id: string): Promise<(() => Promise<void>) | false | undefined> {
    // Looked up first: a linked label only resolves to its source while the source is still here
    const linkedLabels = linkedLabelsOf(this.features, id);
    const restores: Array<(() => Promise<void>) | false | undefined> = [];
    // One at a time, since each delete rewrites this.folders
    for (const label of linkedLabels) {
      restores.push(await this.deleteSingleFeature(label.id));
    }
    const restoreFeature = await this.deleteSingleFeature(id);
    if (!linkedLabels.length || !restoreFeature) return restoreFeature;
    restores.push(restoreFeature);

    return async () => {
      // Reverse order, so the folders snapshot taken first (before anything was removed) is applied last
      for (const restore of restores.reverse()) {
        if (restore) {
          await restore();
        }
      }
    };
  }

  private async deleteSingleFeature(id: string): Promise<(() => Promise<void>) | false | undefined> {
    const feature = this.features.get(id);
    if (!feature) return false;

    const foldersCopy = this.shallowCopyFolders(this.folders);
    // Get the feature's path within folders
    const folderItem = _flattenFolderFeatures(this.folders).get(id);
    if (folderItem) {
      this.removeFolderItem(folderItem);
      await this.update({folders: this.folders});
    }
    // Allow feature to be restored later.
    const graphic = await feature.graphic;
    // Ugly hack - change if/when subscription manager is available on base graphic class
    if (graphic?.subscriptions?.subscriptions) {
      // There may be subscribers trying to save this graphic when it is modified,
      // remove them before attempting to delete
      Array.from(graphic.subscriptions.subscriptions.values()).forEach((sub) => {
        graphic.subscriptions.subscriptions.delete(sub);
      });
    }
    // if the feature is currently being edited, cancel the edit
    if (getStore().state.editingFeature && getStore().state.editingFeature.id === feature.id && !feature.newFeature) {
      await feature.setEditing(false);
      graphic?.cleanup?.();
      getStore().commit('setEditingFeature', null);
    }
    // Close the info window if it is open
    const iWTabIndex = await getStore().dispatch('infowindow/closeInfoWindowTab', feature.id);
    const graphicJson = graphic?.toJSON();
    let restoreFeature;
    if (!graphicJson) {
      console.warn(`Feature ${id} can't be restored because its graphic JSON could not be retrieved.`);
    } else {
      restoreFeature = async () => {
        // Reset a feature's visible status when restored
        const firestoreUpdate = getFirestoreUpdateFromFeature(feature);
        const displayFields = firestoreUpdate.displayFields;

        const featureData = {...firestoreUpdate, ...graphicJson, visible: true};
        featureData.displayFields = displayFields ?? [];

        const newFeature = await this.newFeature(
          {...featureData},
          id,
          folderItem ?? null,
          false,
        );
        this.folders = foldersCopy;
        await this.update({folders: this.folders});
        if (iWTabIndex > -1 && newFeature) {
          newFeature.openInfoWindow({
            payload: {
              location: newFeature.center,
              toolbar: {toolbarOptions: []},
            },
            insertIndex: iWTabIndex,
          });
        }
      };
    }

    getCollection(this.featureCollectionName).removeItems(feature);

    this.features.delete(id);
    await deleteSubcollectionDocument(this.collectionName, this.id, 'features', id);
    await this.update({folders: this.folders}); // force editor and timestamp to be updated
    return restoreFeature;
  }

  /**
   * Updates the render order of all features in the feature manager and applies the calculated z-index to each feature
   * @param prioritize If true, the z-index of this feature manager's features will be prioritized.
   */
  updateFeatureRenderOrder(prioritize = false) {
    Array.from(
      _flattenFolderFeatures(this.folders).keys(), // .keys() returns the feature IDs in order
      (id) => this.features.get(id), // mapFn converts the IDs to Features
    )
      .filter((f) => f)
      // The z-index with the highest value is rendered on top, so we need to invert the index for the feature
      .forEach((f, i, arr) => f?.setZIndex(prioritize || this.editMode, this.parent?.index ?? 0, arr.length - i));
  }

  /**
   * Creates a new default folder object and pushes it to the feature manager folders root
   */
  async createFolder() {
    const random = Math.random().toString();
    const uid = random + Date.now();
    this.folders.unshift({
      name: `Untitled Folder`,
      children: [],
      folderID: uid,
      showBody: false,
      visible: true,
      defaultVisibility: true,
    });
    await this.update({folders: this.folders});
  }

  /**
   * Given an array representing an item's position in the feature manager's folder structure, returns the item
   * at the provided location
   * @param path - The folder item's path in the feature manager structure
   * @return An object representing either a feature or another folder.
   */
  getFolderItem(path: number[]) : {
    parentFolder: Nullable<FolderItem[] | FolderItem>;
    itemAtIndex: Nullable<FolderItem>;
  } | null {
    // The output for this function is an object with a value for the parent folder to the path passed in
    // as well as the actual item at the final index in the path
    const item: {parentFolder: Nullable<FolderItem[] | FolderItem>; itemAtIndex: Nullable<FolderItem>} = {
      parentFolder: null,
      itemAtIndex: null,
    };
    // If there is only one index the item belongs to the root folder and should be handled differently
    if (path.length === 1) {
      const itemIndex = path[0];
      if (!this.folders[itemIndex]) return null;
      item.parentFolder = this.folders;
      item.itemAtIndex = this.folders[itemIndex];
    } else {
      try {
        // Start at the folder item located at the root
        let currentFolder = this.folders[path[0]];
        for (let i = 1; i < path.length - 1; i++) {
          const index = path[i];
          if (isFolder(currentFolder)) {
            currentFolder = currentFolder.children[index];
          }
        }
        // The last assigned value to currentFolder is the parent folder
        item.parentFolder = currentFolder;
        // The item at the index within `currentFolder` is the item whose path was actually passed in.
        // This could be either a feature or another nested folder
        if (isFolder(currentFolder)) {
          item.itemAtIndex = currentFolder.children[path[path.length - 1]];
        } else {
          item.itemAtIndex = null;
        }
      } catch (error) {
        return null;
      }
    }
    return item;
  }

  /**
   * Removes an item from a folder at a given index and returns the item that was removed
   * @param path - The path to the item that should be removed
   * @return The folder item located at the given path
   */
  removeFolderItem(path: number[]) {
    const folderItem = this.getFolderItem(path);
    if (!folderItem) return null;
    const {parentFolder, itemAtIndex} = folderItem;
    const itemIndex = path[path.length - 1];
    if (!parentFolder) return null;
    if (!Array.isArray(parentFolder) && isFolder(parentFolder)) {
      (parentFolder as Folder).children.splice(itemIndex, 1);
    } else if (Array.isArray(parentFolder)) {
      (parentFolder as FolderItem[]).splice(itemIndex, 1);
    }
    return itemAtIndex;
  }

  /**
   * Adds an item to a specified folder
   * @param folder - The folder object to add an item to
   * @param item - The item that should be added to the folder
   * @param index - The index within the folder's children to add the item to
   */
  async addFolderItem(folder: Nullable<FolderItem>, item: Nullable<FolderItem>, index: number | null = null) {
    if (!folder || !item) return;
    // Cases for either adding an item to a sub folder or root
    const target = isFolder(folder) ? folder.children : folder;
    if (Array.isArray(target)) {
      if (index !== null) {
        target.splice(index, 0, item);
      } else {
        target.unshift(item);
      }
    }
    if (isFolder(folder)) {
      folder.showBody = true;
    }
    await this.update({folders: this.folders});
  }

  /**
   * Takes two paths, the 'from' path and the 'to' path and determines if the the item is a valid folder move
   * @param to - The path that the item is currently located at
   * @param from - The path that the item would be moved to
   * @return  Whether or not the operation is a valid move based off of a series of criteria
   */
  isValidFolderOperation(to: number[], from: number[]) {
    // Handle invalid root moves and moves to the same path directly
    if (to.length === from.length && to.every((val, index) => val === from[index])) return false;
    // If the item being moved is an ancestor to where the item is moving to it is not valid
    if (to.length < from.length) {
      for (let i = 0; i < to.length; i++) {
        if (to[i] !== from[i]) {
          return true;
        }
      }
      return false;
    }
    return true;
  }

  /**
   * Checks if the folder structure exceeds a maximum depth of 10.
   * @param folder - The folder object to check.
   * @param depth - The current depth of the folder.
   * @return Returns true if the folder structure is within the maximum depth
   */
  checkMaxDepth(folder: FolderItem, depth = 0) {
    if (depth > 10) {
      return false;
    }
    if (!isFolder(folder)) {
      return true;
    }
    for (let i = 0; i < folder.children.length; i++) {
      if (!this.checkMaxDepth(folder.children[i], depth + 1)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Handles updating either a folder's name or showBody status
   * @param path - The folder's full path
   * @param key - The folder property to update
   * @param value - The value that the folder's key should be updated to
   */
  async updateFolder(path: number[], key: 'name' | 'showBody', value: string | boolean) {
    const folderItem = this.getFolderItem(path);
    if (!folderItem || !folderItem.itemAtIndex) return;

    const {itemAtIndex} = folderItem;
    itemAtIndex[key] = value;
    if (this.editMode) {
      await this.update({folders: this.folders});
    }
  }

  shallowCopyFolders(originalFolders: FolderItem[]): FolderItem[] {
    return originalFolders.map((folder) => {
      if (isFolder(folder)) {
        return {...folder, children: this.shallowCopyFolders(folder.children)};
      } else {
        return {...folder};
      }
    });
  }

  /**
   * Helper function for returning the depth of a given folder
   * @param folder - the folder object to begin the depth calculation from
   * @param depth - the current depth of the folder
   * @return the maximum depth of the folder
   */
  folderDepth(folder: Folder, depth: number = 0): number {
    if (!folder.children) {
      return depth;
    }
    let maxDepth = depth;
    for (let i = 0; i < folder.children.length; i++) {
      if (!isFolder(folder.children[i])) continue;
      maxDepth = Math.max(maxDepth, this.folderDepth(folder.children[i] as Folder, depth + 1));
    }
    return maxDepth;
  }

  /**
   * @param path - The folder's path that should be deleted
   * @return A restore callback that can be used to restore the folder
   */
  async deleteFolder(path: number[]): Promise<(() => Promise<void>) | undefined> {
    const foldersCopy = this.shallowCopyFolders(this.folders);
    const folderItem = this.getFolderItem(path);
    if (!folderItem || !folderItem.itemAtIndex || !folderItem.parentFolder) return;

    const {itemAtIndex} = folderItem;
    let allFeatures: string[] = [];
    if (isFolder(itemAtIndex)) {
      allFeatures = Array.from(_flattenFolderFeatures(itemAtIndex.children).keys());
    }
    const callbacks: Array<() => Promise<void>> = [];
    await Promise.all(allFeatures.map((f) => {
      return (async () => {
        const feature = this.features.get(f);
        if (!feature) return;
        const graphic = await feature.graphic;
        const graphicJson = graphic.toJSON();
        this.deleteFeature(f);
        callbacks.push(async () => {
          await this.newFeature(
            {...graphicJson, ...getFirestoreUpdateFromFeature(feature), visible: true},
            f,
            null,
            true,
          );
        });
      })();
    }));

    this.removeFolderItem(path);

    const restoreFn = async () => {
      await Promise.all(callbacks.map((c) => c()));
      this.folders = foldersCopy;
      await this.update({folders: this.folders});
    };
    await this.update({folders: this.folders});
    return restoreFn;
  }


  async toggleFolderVisibility(path: number[], newState: boolean, persist = false) {
    const folder = this.getFolderItem(path)?.itemAtIndex;
    if (!folder || !isFolder(folder)) return;
    folder.visible = newState;
    const setChildrenVisibility = (arr: FolderItem[]) => {
      for (let i = 0; i <= arr.length - 1; i++) {
        if (isFolder(arr[i])) {
          (arr[i] as Folder).visible = newState;
          setChildrenVisibility((arr[i] as Folder).children);
        } else {
          const feature = this.features.get((arr[i] as FeatureFolderItem).id);
          if (feature) {
            feature.setVisibility(newState);
            if (persist) {
              feature.setDefaultVisibility(newState);
            }
          }
        }
      }
    };
    setChildrenVisibility(folder.children);
    if (persist) {
      folder.defaultVisibility = newState;
      await this.update({folders: this.folders});
    }
    return;
  }
}
